import React, { useState, useEffect, useRef } from 'react';
import { ChatBubbleOvalLeftIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import io from 'socket.io-client';
import { useTheme } from '../contexts/ThemeContext';
import { getApiUrl, getSocketUrl } from '../utils/api';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

const AIChatbot: React.FC = () => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: '�� 안녕하세요! 저는 exTrader의 AI 어시스턴트입니다.\n\n🤖 **현재 기능:**\n• 실시간 시장 분석 및 가격 예측\n• 개인화된 거래 전략 추천\n• 리스크 관리 조언\n• 최신 암호화폐 뉴스 요약\n\n💡 **사용법:** 아래 추천 질문을 클릭하거나 자유롭게 질문해보세요!',
      isBot: true,
      timestamp: new Date(),
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isOnline] = useState(true);
  const [currentModel, setCurrentModel] = useState('GPT-4o');
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // 추천 질문들 (이모지 포함)
  const suggestedQuestions = [
    { text: "가격분석", emoji: "📊", fullText: "현재 BTC 가격 분석해줘" },
    { text: "거래전략", emoji: "🎯", fullText: "거래 전략 추천해줘" },
    { text: "시장동향", emoji: "📈", fullText: "시장 동향은 어때?" },
    { text: "리스크관리", emoji: "🛡️", fullText: "리스크 관리 방법 알려줘" },
    { text: "뉴스요약", emoji: "📰", fullText: "최근 뉴스 요약해줘" },
    { text: "포트폴리오", emoji: "💼", fullText: "포트폴리오 조언 부탁해" }
  ];

  // 자동 스크롤
  useEffect(() => {
    if (chatContainerRef.current) {
      const scrollElement = chatContainerRef.current;
      scrollElement.scrollTo({
        top: scrollElement.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, isTyping]);

  // 현재 설정 가져오기
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/settings`);
        if (response.ok) {
          const settings = await response.json();
          console.log('📝 받은 설정:', settings);
          
          // 새로운 AI 제공자 설정 확인
          if (settings.aiProviderSettings) {
            // 활성화된 AI 제공자 찾기
            const enabledProviders = [];
            
            if (settings.aiProviderSettings.openai?.enabled && settings.aiProviderSettings.openai?.apiKey) {
              enabledProviders.push(`OpenAI ${settings.aiProviderSettings.openai.model || 'gpt-4o-mini'}`);
            }
            if (settings.aiProviderSettings.anthropic?.enabled && settings.aiProviderSettings.anthropic?.apiKey) {
              enabledProviders.push(`Claude ${settings.aiProviderSettings.anthropic.model || 'claude-3-haiku'}`);
            }
            if (settings.aiProviderSettings.google?.enabled && settings.aiProviderSettings.google?.apiKey) {
              enabledProviders.push(`Gemini ${settings.aiProviderSettings.google.model || 'gemini-1.5-flash'}`);
            }
            if (settings.aiProviderSettings.xai?.enabled && settings.aiProviderSettings.xai?.apiKey) {
              enabledProviders.push(`Grok ${settings.aiProviderSettings.xai.model || 'grok-beta'}`);
            }
            if (settings.aiProviderSettings.deepseek?.enabled && settings.aiProviderSettings.deepseek?.apiKey) {
              enabledProviders.push(`DeepSeek ${settings.aiProviderSettings.deepseek.model || 'deepseek-chat'}`);
            }
            if (settings.aiProviderSettings.ollama?.enabled) {
              enabledProviders.push(`Ollama ${settings.aiProviderSettings.ollama.model || 'llama3.2'}`);
            }
            
            // 챗봇 AI 제공자 설정 확인
            if (settings.chatbotAiProvider && settings.aiProviderSettings[settings.chatbotAiProvider]?.enabled) {
              const provider = settings.aiProviderSettings[settings.chatbotAiProvider];
              const providerNames: { [key: string]: string } = {
                'openai': 'OpenAI',
                'anthropic': 'Claude',
                'google': 'Gemini',
                'xai': 'Grok',
                'deepseek': 'DeepSeek',
                'ollama': 'Ollama'
              };
              const providerName = providerNames[settings.chatbotAiProvider as string] || settings.chatbotAiProvider;
              setCurrentModel(`${providerName} ${provider.model || 'default'}`);
            } else if (settings.primaryAiProvider && settings.aiProviderSettings[settings.primaryAiProvider]?.enabled) {
              const provider = settings.aiProviderSettings[settings.primaryAiProvider];
              const providerNames: { [key: string]: string } = {
                'openai': 'OpenAI',
                'anthropic': 'Claude',
                'google': 'Gemini',
                'xai': 'Grok',
                'deepseek': 'DeepSeek',
                'ollama': 'Ollama'
              };
              const providerName = providerNames[settings.primaryAiProvider as string] || settings.primaryAiProvider;
              setCurrentModel(`${providerName} ${provider.model || 'default'}`);
            } else if (enabledProviders.length > 0) {
              setCurrentModel(enabledProviders[0]);
            } else {
              setCurrentModel('규칙 기반 분석');
            }
          } else {
            // 레거시 설정 지원
            if (settings.aiModel === 'chatgpt') {
              setCurrentModel(settings.chatgptModel || 'GPT-4o');
            } else {
              setCurrentModel('규칙 기반 분석');
            }
          }
          
          console.log('📝 현재 AI 설정:', currentModel);
        }
      } catch (error) {
        console.error('설정 가져오기 실패:', error);
        setCurrentModel('GPT-4o');
      }
    };

    fetchSettings();

    // WebSocket을 통한 실시간 설정 동기화
    let socket: any = null;
    
    try {
      const serverUrl = getSocketUrl();
      
      socket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        timeout: 10000
      });

      // 설정 업데이트 수신
      socket.on('settingsUpdate', (settings: any) => {
        console.log('🤖 AI 챗봇 설정 업데이트 수신:', settings);
        
        // 새로운 AI 제공자 설정으로 업데이트
        if (settings.aiProviderSettings) {
          // 챗봇 AI 제공자 설정 확인
          if (settings.chatbotAiProvider && settings.aiProviderSettings[settings.chatbotAiProvider]?.enabled) {
            const provider = settings.aiProviderSettings[settings.chatbotAiProvider];
            const providerNames: { [key: string]: string } = {
              'openai': 'OpenAI',
              'anthropic': 'Claude',
              'google': 'Gemini',
              'xai': 'Grok',
              'deepseek': 'DeepSeek',
              'ollama': 'Ollama'
            };
            const providerName = providerNames[settings.chatbotAiProvider as string] || settings.chatbotAiProvider;
            setCurrentModel(`${providerName} ${provider.model || 'default'}`);
          } else if (settings.primaryAiProvider && settings.aiProviderSettings[settings.primaryAiProvider]?.enabled) {
            const provider = settings.aiProviderSettings[settings.primaryAiProvider];
            const providerNames: { [key: string]: string } = {
              'openai': 'OpenAI',
              'anthropic': 'Claude',
              'google': 'Gemini',
              'xai': 'Grok',
              'deepseek': 'DeepSeek',
              'ollama': 'Ollama'
            };
            const providerName = providerNames[settings.primaryAiProvider as string] || settings.primaryAiProvider;
            setCurrentModel(`${providerName} ${provider.model || 'default'}`);
          } else {
            setCurrentModel('규칙 기반 분석');
          }
        } else {
          // 레거시 설정 지원
          if (settings.aiModel === 'chatgpt') {
            setCurrentModel(settings.chatgptModel || 'GPT-4o');
          } else {
            setCurrentModel('규칙 기반 분석');
          }
        }
      });

      // 초기 데이터에서 설정 받기
      socket.on('initialData', (data: any) => {
        if (data.settings) {
          console.log('🤖 AI 챗봇 초기 설정 수신:', data.settings);
          // 동일한 로직 적용
          if (data.settings.aiProviderSettings) {
            if (data.settings.chatbotAiProvider && data.settings.aiProviderSettings[data.settings.chatbotAiProvider]?.enabled) {
              const provider = data.settings.aiProviderSettings[data.settings.chatbotAiProvider];
              const providerNames: { [key: string]: string } = {
                'openai': 'OpenAI',
                'anthropic': 'Claude',
                'google': 'Gemini',
                'xai': 'Grok',
                'deepseek': 'DeepSeek',
                'ollama': 'Ollama'
              };
              const providerName = providerNames[data.settings.chatbotAiProvider as string] || data.settings.chatbotAiProvider;
              setCurrentModel(`${providerName} ${provider.model || 'default'}`);
            } else if (data.settings.primaryAiProvider && data.settings.aiProviderSettings[data.settings.primaryAiProvider]?.enabled) {
              const provider = data.settings.aiProviderSettings[data.settings.primaryAiProvider];
              const providerNames: { [key: string]: string } = {
                'openai': 'OpenAI',
                'anthropic': 'Claude',
                'google': 'Gemini',
                'xai': 'Grok',
                'deepseek': 'DeepSeek',
                'ollama': 'Ollama'
              };
              const providerName = providerNames[data.settings.primaryAiProvider as string] || data.settings.primaryAiProvider;
              setCurrentModel(`${providerName} ${provider.model || 'default'}`);
            } else {
              setCurrentModel('규칙 기반 분석');
            }
          } else {
            if (data.settings.aiModel === 'chatgpt') {
              setCurrentModel(data.settings.chatgptModel || 'GPT-4o');
            } else {
              setCurrentModel('규칙 기반 분석');
            }
          }
        }
      });

    } catch (error) {
      console.error('AI 챗봇 WebSocket 연결 오류:', error);
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim()) return;

    console.log('📤 메시지 전송 시작:', textToSend);

    const userMessage: Message = {
      id: Date.now().toString(),
      text: textToSend,
      isBot: false,
      timestamp: new Date(),
    };

    console.log('👤 사용자 메시지 생성:', userMessage);
    setMessages(prev => {
      console.log('📝 현재 메시지 목록:', prev);
      const newMessages = [...prev, userMessage];
      console.log('📝 업데이트된 메시지 목록:', newMessages);
      return newMessages;
    });
    setInputMessage('');
    setIsTyping(true);

    try {
      console.log('🌐 서버로 요청 전송...');
      const response = await fetch(`${getApiUrl()}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: textToSend }),
      });

      console.log('📡 서버 응답 상태:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📥 서버 응답 데이터:', data);
        console.log('📄 응답 텍스트:', data.response);
        
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: data.response || data.reply || '응답을 받지 못했습니다.',
          isBot: true,
          timestamp: new Date(),
        };

        console.log('💬 생성된 봇 메시지:', botMessage);
        setMessages(prev => {
          console.log('🤖 봇 메시지 추가 전 목록:', prev);
          const newMessages = [...prev, botMessage];
          console.log('🤖 봇 메시지 추가 후 목록:', newMessages);
          return newMessages;
        });
      } else {
        console.error('❌ 서버 응답 오류:', response.status, response.statusText);
        throw new Error('서버 응답 오류');
      }
    } catch (error) {
      console.error('💥 Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: '죄송합니다. 일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        isBot: true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      console.log('✅ isTyping 상태 false로 변경');
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const handleSuggestedQuestion = (questionObj: { text: string; emoji: string; fullText: string }) => {
    setInputMessage(questionObj.fullText);
    setTimeout(() => {
      sendMessage(questionObj.fullText);
    }, 100);
  };

  const handleSendMessage = () => {
    sendMessage();
  };

  return (
    <div className={`h-full flex flex-col ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg overflow-hidden`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center space-x-2">
          <ChatBubbleOvalLeftIcon className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            AI 어시스턴트
          </h3>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-2 py-1 rounded text-xs font-medium ${
            isOnline ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                     : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
          }`}>
            {isOnline ? '온라인' : '오프라인'}
          </div>
          <div className={`px-2 py-1 rounded text-xs font-medium border ${
            theme === 'dark' 
              ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
              : 'bg-blue-50 text-blue-600 border-blue-200'
          }`}>
            🤖 {currentModel}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div 
        className={`flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent ${
          theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
        }`} 
        ref={chatContainerRef}
        style={{ 
          maxHeight: 'calc(100% - 200px)', // 헤더 + 추천질문 + 입력창 높이를 제외한 나머지
          minHeight: '200px',
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg p-2.5 ${
                message.isBot
                  ? theme === 'dark'
                    ? 'bg-gray-700 text-white'
                    : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
                  : theme === 'dark'
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
              }`}
            >
              {message.isBot ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => <p className="mb-1 last:mb-0 text-sm">{children}</p>,
                    ul: ({ children }) => <ul className="mb-1 last:mb-0 pl-3 text-sm">{children}</ul>,
                    ol: ({ children }) => <ol className="mb-1 last:mb-0 pl-3 text-sm">{children}</ol>,
                    li: ({ children }) => <li className="mb-0.5 text-sm">{children}</li>,
                    code: ({ children }) => (
                      <code className={`px-1 py-0.5 rounded text-xs ${
                        theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                      }`}>
                        {children}
                      </code>
                    ),
                    pre: ({ children }) => (
                      <pre className={`p-2 rounded overflow-x-auto text-xs ${
                        theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                      }`}>
                        {children}
                      </pre>
                    ),
                  }}
                >
                  {message.text}
                </ReactMarkdown>
              ) : (
                <p className="whitespace-pre-wrap text-sm">{message.text}</p>
              )}
              <div className={`text-xs mt-1 ${
                message.isBot 
                  ? theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  : 'text-blue-100'
              }`}>
                {message.timestamp.toLocaleTimeString('ko-KR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className={`max-w-[85%] rounded-lg p-2.5 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full animate-bounce ${
                  theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                }`} style={{ animationDelay: '0ms' }}></div>
                <div className={`w-2 h-2 rounded-full animate-bounce ${
                  theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                }`} style={{ animationDelay: '150ms' }}></div>
                <div className={`w-2 h-2 rounded-full animate-bounce ${
                  theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                }`} style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Suggested Questions */}
      {suggestedQuestions.length > 0 && (
        <div className={`px-3 py-2 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            💡 추천 질문
          </p>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {suggestedQuestions.map((questionObj, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuestion(questionObj)}
                className={`flex-shrink-0 flex items-center space-x-1 text-left px-2 py-1.5 text-xs rounded transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-white hover:bg-gray-100 text-gray-700'
                } border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'} whitespace-nowrap`}
              >
                <span className="text-xs">{questionObj.emoji}</span>
                <span>{questionObj.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className={`border-t p-3 ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="메시지를 입력하세요..."
            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            disabled={isTyping}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping}
            className={`px-3 py-2 rounded-lg font-medium transition-colors text-sm ${
              !inputMessage.trim() || isTyping
                ? theme === 'dark'
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : theme === 'dark'
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            전송
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatbot; 